/* eslint-disable no-unsafe-optional-chaining */
import Decimal from 'decimal.js';
import { camelToSnakeCase, getSortedData } from '../../../RevisaoPrecos/utils';
import { NegotitationEditDatapointProps, NegotitationRowDataProps, SuppliersResponseContentProps } from './NegociacaoFornecedor.types';
import { CostNegotiationMode, DEFAULT_COST_NEGOTIATION_MODE } from './costNegotiation';

export const formatDecimal = (value: number, decimals: number) => {
  if (value == null || !Number.isFinite(value)) return '0,00';
  return new Decimal(value).toDecimalPlaces(decimals).toFixed(decimals).toString().replace('.', ',');
};

/** Diz se algum insumo do cálculo não veio. */
const hasMissingInput = (...values: Array<number | null | undefined>) => values.some((value) => value === null || value === undefined);

export const newPriceCalc = (data: SuppliersResponseContentProps, value: number) => {
  if (hasMissingInput(data.salesTaxPercentage, data.expensesPercent)) return null;

  const totalPerc = new Decimal(data.newObjectiveMargin ?? 0).plus(data.salesTaxPercentage).plus(data.expensesPercent);

  const denom = new Decimal(100).minus(totalPerc);
  if (denom.lte(0) || !denom.isFinite()) return null;

  return new Decimal(value).dividedBy(denom).times(100).toDecimalPlaces(2).toNumber();
};

export const newCostCalc = (data: SuppliersResponseContentProps, margin: number, newPrice: number) => {
  if (hasMissingInput(margin, data.salesTaxPercentage, data.expensesPercent)) return 0;

  const totalPerc = new Decimal(margin).plus(data.salesTaxPercentage).plus(data.expensesPercent);

  return new Decimal(newPrice).times(new Decimal(100).minus(totalPerc)).dividedBy(100).toDecimalPlaces(2).toNumber();
};

export const PMZNewCostCalc = (data: SuppliersResponseContentProps, value: number) => {
  if (hasMissingInput(value, data.salesTaxPercentage, data.expensesPercent)) return 0;

  const totalPerc = new Decimal(data.salesTaxPercentage).plus(data.expensesPercent);

  return new Decimal(value).dividedBy(new Decimal(100).minus(totalPerc)).times(100).toDecimalPlaces(2).toNumber();
};

export const priceMarginCalc = (data: SuppliersResponseContentProps, price: number, cost: number) => {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (hasMissingInput(cost, data.salesTaxPercentage, data.expensesPercent)) return null;

  const priceDec = new Decimal(price);
  const costDec = new Decimal(cost);
  const taxPerc = new Decimal(data.salesTaxPercentage).plus(data.expensesPercent);

  const tax = priceDec.times(taxPerc).dividedBy(100);
  const liquidPrice = priceDec.minus(tax);

  return liquidPrice.minus(costDec).dividedBy(priceDec).times(100).toDecimalPlaces(2).toNumber();
};

export const priceMarginCalcWithNewPMZ = (price: number, newPMZ: number) => {
  const priceDecimal = new Decimal(price).toDecimalPlaces(2);
  if (priceDecimal.isZero()) return null;
  const newPMZDecimal = new Decimal(newPMZ).toDecimalPlaces(2);
  return priceDecimal.minus(newPMZDecimal).dividedBy(priceDecimal).times(100).toNumber();
};

export const newPriceCompetitorsCalc = (data: SuppliersResponseContentProps, newPrice: number) => {
  const competitorsPrice = data.competitorsPrice;
  if (competitorsPrice === null || competitorsPrice === 0) return null;
  const competitorsPriceDecimal = new Decimal(competitorsPrice).toDecimalPlaces(2);
  if (competitorsPriceDecimal.isZero()) return null;
  const newPriceDecimal = new Decimal(newPrice).toDecimalPlaces(2);
  return newPriceDecimal.dividedBy(competitorsPriceDecimal).times(100).toNumber();
};

export const bestIdealPriceCalc = (data: SuppliersResponseContentProps, newObjectiveMargin: number, costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE) => {
  const basePrice = data.competitorsPrice ?? data.retailPrice;
  if (!basePrice) return 0;

  // Sem um dos percentuais não dá para dizer qual é o preço ideal de compra, e completar com zero
  // devolveria um preço calculado como se não houvesse despesa nem imposto de venda: um número
  // errado com aparência de certo, na célula que o comprador usa para decidir. Devolver zero aqui
  // faz a célula exibir "R$ --", que é o que ela já exibe quando falta o preço base logo acima.
  if (hasMissingInput(newObjectiveMargin, data.expensesPercent, data.salesTaxPercentage)) return 0;

  const perc = new Decimal(newObjectiveMargin).plus(data.expensesPercent).plus(data.salesTaxPercentage);

  if (!perc.isFinite()) return 0;

  const liquidCost = new Decimal(basePrice).times(new Decimal(100).minus(perc)).dividedBy(100);

  if (costNegotiationMode === CostNegotiationMode.ENTRY_COST) {
    const purchaseTaxFactor = new Decimal(1).plus(new Decimal(data.purchaseTaxPercentage ?? 0).dividedBy(100));
    if (purchaseTaxFactor.isZero()) return 0;

    return liquidCost.dividedBy(purchaseTaxFactor).toDecimalPlaces(2).toNumber();
  }

  return liquidCost.toDecimalPlaces(2).toNumber();
};

export const bestIdealPricePMZCalc = (data: SuppliersResponseContentProps, price: number, costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE) => {
  const priceDecimal = new Decimal(price);
  if (priceDecimal.isZero()) return 0;

  // Mesma regra da função acima, e pelos mesmos dois campos: sem eles não há preço de margem zero a
  // exibir, e inventar zero mentiria sobre a margem.
  if (hasMissingInput(data.expensesPercent, data.salesTaxPercentage)) return 0;

  const expensesPercentDecimal = new Decimal(data.expensesPercent);
  const salesTaxPercentageDecimal = new Decimal(data.salesTaxPercentage);

  const totalPercent = expensesPercentDecimal.plus(salesTaxPercentageDecimal);
  const denominator = new Decimal(100).minus(totalPercent);

  if (denominator.isZero()) return 0;

  if (costNegotiationMode === CostNegotiationMode.ENTRY_COST) {
    const purchaseTaxFactor = new Decimal(1).plus(new Decimal(data.purchaseTaxPercentage ?? 0).dividedBy(100));
    const liquidCost = priceDecimal.times(purchaseTaxFactor);

    return liquidCost.dividedBy(denominator).times(100).toDecimalPlaces(2).toNumber();
  }

  return priceDecimal.dividedBy(denominator).times(100).toDecimalPlaces(2).toNumber();
};

export const calculateVariationPercentage = (cost: number, newCost: number) => {
  const costDecimal = new Decimal(cost).toDecimalPlaces(2);
  if (costDecimal.isZero()) return 0;
  return new Decimal(newCost).minus(costDecimal).dividedBy(costDecimal).times(100).toNumber();
};

export const calculateVariation = (newNumber: number, originalNumber: number) => new Decimal(newNumber).minus(new Decimal(originalNumber)).toNumber();

export const competitivenessIndicator = (value: string) => {
  const indicator = {
    RULE_CPI_MAX: '>',
    RULE_CPI_MIN: '<',
    RULE_CPI_AVG: 'x̅',
  } as const;
  return (indicator as Record<string, string>)[value] ?? '';
};

type SortMode = 'snake' | 'camel';

export const getFiltrosModel = ({ size = 20, sort, page = 0, ...filter }: Record<string, any>, sortMode: SortMode = 'snake') => {
  const CURRENT_SORT = {
    snake: camelToSnakeCase(getSortedData({ sort }) ?? ''),
    camel: getSortedData({ sort }) ?? '',
  } as const;
  const filterWithoutResponsible = Object.keys(filter).reduce<Record<string, any>>((result, key) => {
    if (key !== 'responsible') result[key] = filter[key];
    return result;
  }, {});

  return {
    page: page <= 0 ? 0 : page - 1,
    sort: CURRENT_SORT[sortMode],
    size,
    ...filterWithoutResponsible,
  };
};

export const getEditedDatapointModel = (
  rowData: NegotitationRowDataProps,
  // A verba nao mora na linha: ela vive no estado de edicao da grade, para que desfazer saiba
  // para onde voltar. Chega aqui por fora, e ausente quer dizer "o comprador nao mexeu nela".
  verba?: Pick<NegotitationEditDatapointProps, 'sellinAllowance' | 'sellinAllowanceOrigin'> | null,
): NegotitationEditDatapointProps => ({
  id: rowData.productsToBePricedId,
  ...(verba ?? {}),
  newObjectiveMargin: rowData.newObjectiveMargin,
  newCost: rowData.newCost,
  newPrice: rowData.newPrice,
  newPriceMargin: rowData.newPriceMargin,
  newPriceCompetitors: rowData.newPriceCompetitors,
  bestIdealPrice: rowData.bestIdealPrice,
  bestIdealPricePMZ: rowData.bestIdealPricePMZ,
  PMZNewCost: rowData.PMZNewCost,
  costIndicator: rowData.costIndicator,
  visionSaved: rowData.visionSaved,
  cluster: rowData.cluster,
  negotiationSupplierDatapointStatus: rowData.negotiationSupplierDatapointStatus,
});
